import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import {
  FindManyMessageThreads,
  Message,
  PaginateResult,
} from '@univacity/shared/types';
import { EntityStore } from '../entity-store';

import { MessagesActions } from './messages.actions';
import { MessagesEntity } from './messages.models';

export const MESSAGES_FEATURE_KEY = 'messages';

export interface MessagesState
  extends EntityState<MessagesEntity>,
    EntityStore {
  unread: number; // which Messages record has been selected
  query?: FindManyMessageThreads;
  inbox: Message[];
  messagePagination?: Omit<PaginateResult<Message>, 'docs'>;
}

export interface MessagesPartialState {
  readonly [MESSAGES_FEATURE_KEY]: MessagesState;
}

export const messagesAdapter: EntityAdapter<MessagesEntity> =
  createEntityAdapter<MessagesEntity>({
    sortComparer: (a, b) =>
      new Date(a.thread.updatedAt) > new Date(b.thread.updatedAt) ? -1 : 1,
  });

export const initialMessagesState: MessagesState =
  messagesAdapter.getInitialState({
    // set initial required properties
    loading: false,
    unread: 0,
    inbox: [],
  });

const reducer = createReducer(
  initialMessagesState,
  on(MessagesActions.fetchThreads, (state, { query }) => ({
    ...state,
    loading: true,
    error: null,
    query,
  })),
  on(MessagesActions.fetchThreadsSuccess, (state, { res }) => {
    const { docs, ...paginate } = res;
    return messagesAdapter.setMany(
      docs.map((thread) => ({
        id: thread._id,
        thread,
        unread: 0,
        inbox: [],
      })),
      { ...state, ...paginate, loading: false }
    );
  }),
  on(MessagesActions.fetchThreadsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  on(MessagesActions.selectThread, (state, { thread }) => ({
    ...state,
    selected: thread,
  })),

  on(MessagesActions.fetchAllUnreadMessagesCount, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    MessagesActions.fetchAllUnreadMessagesCountSuccess,
    (state, { count }) => ({ ...state, loading: true, unread: count })
  ),
  on(
    MessagesActions.fetchAllUnreadMessagesCountFailure,
    (state, { error }) => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(MessagesActions.fetchAllUnreadMessagesCount, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    MessagesActions.fetchAllUnreadMessagesCountSuccess,
    (state, { count }) => ({ ...state, loading: true, unread: count })
  ),
  on(
    MessagesActions.fetchAllUnreadMessagesCountFailure,
    (state, { error }) => ({
      ...state,
      error,
      loading: false,
    })
  ),

  on(MessagesActions.fetchMessages, (state, { thread }) => ({
    ...state,
    loading: true,
    error: null,
    selected: thread,
  })),
  on(MessagesActions.fetchMessagesSuccess, (state, { res }) => {
    const { docs, ...paginate } = res;
    const map = new Map<string, Message>();
    [...state.inbox, ...docs].forEach((m) => map.set(m._id, m));
    const inbox: Message[] = [...map.values()];
    return { ...state, loading: false, inbox, messagePagination: paginate };
  }),
  on(MessagesActions.fetchMessagesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  on(MessagesActions.sendMessage, (state, { thread }) => ({
    ...state,
    loading: true,
    error: null,
    selected: thread,
  })),
  on(MessagesActions.sendMessageSuccess, (state, { message }) => {
    const entity = state.entities[message.thread] as MessagesEntity;
    return messagesAdapter.updateOne(
      {
        id: message.thread,
        changes: {
          thread: {
            ...entity.thread,
            lastMessage: message,
            updatedAt: message.createdAt,
          },
          unread: 0,
        },
      },
      {
        ...state,
        inbox: [...state.inbox, message],
        unread: state.unread - entity.unread,
        loading: false,
        error: null,
      }
    );
  }),
  on(MessagesActions.sendMessageFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  on(MessagesActions.fetchUnreadMessagesCount, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    MessagesActions.fetchUnreadMessagesCountSuccess,
    (state, { count, thread }) =>
      messagesAdapter.updateOne(
        {
          id: thread,
          changes: {
            unread: count,
          },
        },
        { ...state, loading: false }
      )
  ),
  on(MessagesActions.fetchUnreadMessagesCountFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  }))
);

export function messagesReducer(
  state: MessagesState | undefined,
  action: Action
) {
  return reducer(state, action);
}
